import axios from 'axios'
import { getToken } from "../../auth/auth"
import { Stage } from '../../config/config'
import { getSiteConfig } from '../../community/siteProvider'

export const USER_PREFERENCES_SCOPE = 'user'
export const SHARED_PREFERENCES_SCOPE = 'shared'

const preferencesFolderByScope = {
  [USER_PREFERENCES_SCOPE]: 'Users',
  [SHARED_PREFERENCES_SCOPE]: 'Settings'
}

const getPreferencesUrl = ({ scope = USER_PREFERENCES_SCOPE, userId, fileName }) => {
  const { siteId, libraryDrives } = getSiteConfig()
  const driveId = libraryDrives[0].driveId
  const folder = preferencesFolderByScope[scope]
  if (!folder) {
    throw new Error(`Unsupported preferences scope: ${scope}`)
  }

  const GRAPH_API_BASE_URL = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root:/${folder}/`
  const GRAPH_API_CONTENT_SUFFIX = `:/content`
  const safeFileName = encodeURIComponent(String(fileName))

  if (scope === USER_PREFERENCES_SCOPE) {
    if (!userId) {
      throw new Error('Could not resolve user id for user preferences')
    }
    const safeUserId = encodeURIComponent(String(userId))
    return `${GRAPH_API_BASE_URL}${safeUserId}/${safeFileName}${GRAPH_API_CONTENT_SUFFIX}`
  }

  return `${GRAPH_API_BASE_URL}${safeFileName}${GRAPH_API_CONTENT_SUFFIX}`
}

const decodeJwtPayload = (jwt) => {
  if (!jwt || typeof jwt !== 'string') return undefined
  const parts = jwt.split('.')
  if (parts.length < 2) return undefined
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const json = atob(padded)
    return JSON.parse(json)
  } catch (_error) {
    return undefined
  }
}

const getUniqueUserIds = (...candidates) => {
  const flattened = candidates.flat().filter(Boolean).map((v) => String(v).trim())
  return Array.from(new Set(flattened))
}

const getTokenContext = async () => {
  const local = window.sessionStorage.mockGraphToken
  if (local) {
    try {
      const parsed = JSON.parse(local)
      if (typeof parsed === 'string') {
        const payload = decodeJwtPayload(parsed)
        return {
          accessToken: parsed,
          userIds: getUniqueUserIds(payload?.oid, payload?.sub)
        }
      }
      const accessPayload = decodeJwtPayload(parsed?.accessToken)
      return {
        accessToken: parsed?.accessToken,
        userIds: getUniqueUserIds(
          accessPayload?.oid,
          parsed?.idTokenClaims?.oid,
          parsed?.uniqueId
        )
      }
    } catch (_error) {
      const payload = decodeJwtPayload(local)
      return {
        accessToken: local,
        userIds: getUniqueUserIds(payload?.oid, payload?.sub)
      }
    }
  }

  const token = await getToken()
  const accessPayload = decodeJwtPayload(token?.accessToken)
  return {
    accessToken: token?.accessToken,
    userIds: getUniqueUserIds(
      accessPayload?.oid,
      token?.idTokenClaims?.oid,
      token?.uniqueId
    )
  }
}

const getRequestOptions = (accessToken) => ({
  headers: {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": 'application/json'
  }
})

const getPreferencesByUrl = async (url, options) => {
  try {
    const { data } = await axios.get(url, options)
    return data
  } catch (error) {
    const status = error?.response?.status
    if ([403, 404].includes(status)) {
      return undefined
    }
    throw new Error(error.message)
  }
}

const tryGetUserScopedPreferences = async (userIds, fileName, options) => {
  for (const candidateUserId of userIds) {
    const url = getPreferencesUrl({
      scope: USER_PREFERENCES_SCOPE,
      userId: candidateUserId,
      fileName
    })
    const data = await getPreferencesByUrl(url, options)
    if (data !== undefined) return data
  }
  return undefined
}

export const getPreferences = async ({ scope = USER_PREFERENCES_SCOPE, userId, fileName }) => {
  const tokenContext = await getTokenContext()
  const accessToken = tokenContext?.accessToken
  const effectiveUserIds = getUniqueUserIds(tokenContext?.userIds, userId)

  if (accessToken) {
    const options = getRequestOptions(accessToken)

    if (scope === USER_PREFERENCES_SCOPE) {
      if (!effectiveUserIds.length) return undefined
      return await tryGetUserScopedPreferences(effectiveUserIds, fileName, options)
    }

    const sharedUrl = getPreferencesUrl({
      scope: SHARED_PREFERENCES_SCOPE,
      fileName
    })
    return await getPreferencesByUrl(sharedUrl, options)
  }

  if (Stage === 'prod') {
    throw new Error('Could not retrieve access token')
  }
}

export const savePreferences = async ({
  scope = USER_PREFERENCES_SCOPE,
  userId,
  fileName,
  preferences,
  getAfterSave = false
}) => {
  const tokenContext = await getTokenContext()
  const accessToken = tokenContext?.accessToken
  const effectiveUserIds = getUniqueUserIds(tokenContext?.userIds, userId)
  const effectiveUserId = effectiveUserIds[0]

  if (accessToken) {
    const options = getRequestOptions(accessToken)
    const url = getPreferencesUrl({
      scope,
      userId: scope === USER_PREFERENCES_SCOPE ? effectiveUserId : undefined,
      fileName
    })

    await axios.put(url, preferences, options)

    if (getAfterSave) {
      return await getPreferences({ scope, userId, fileName })
    }
    return
  }

  if (Stage === 'prod') {
    throw new Error('Could not retrieve access token')
  } else if (getAfterSave) {
    return await getPreferences({ scope, userId, fileName })
  }
}

export const getUserPreferences = async (userId, fileName) => {
  return await getPreferences({
    scope: USER_PREFERENCES_SCOPE,
    userId,
    fileName
  })
}

export const saveUserPreferences = async (userId, fileName, userPreferences, getAfterSave = false) => {
  return await savePreferences({
    scope: USER_PREFERENCES_SCOPE,
    userId,
    fileName,
    preferences: userPreferences,
    getAfterSave
  })
}

export const getSharedPreferences = async (fileName) => {
  return await getPreferences({
    scope: SHARED_PREFERENCES_SCOPE,
    fileName
  })
}

export const saveSharedPreferences = async (fileName, sharedPreferences, getAfterSave = false) => {
  return await savePreferences({
    scope: SHARED_PREFERENCES_SCOPE,
    fileName,
    preferences: sharedPreferences,
    getAfterSave
  })
}

// FAVORITES preferences
const getFavoritesFileName = (group) => `${group}_favorites.json`

// GROUP CONSTANTS
export const SAFETYRESOURCES = 'SafetyResources'
export const SHARELIST = 'SHARELIST'

export const getFavorites = async (userId, group) => {
  const groupFavoritesFileName = getFavoritesFileName(group)
  const preferences = await getUserPreferences(userId, groupFavoritesFileName)
  let favorites = []
  if (preferences) {
    favorites = preferences['favorites'] ?? []
  } else {
    // Initialize a missing preferences file so future reads do not 404.
    try {
      await saveUserPreferences(userId, groupFavoritesFileName, { favorites: [] })
    } catch (_error) {
      // Non-blocking: keep returning an empty favorites list.
    }
  }
  return favorites
}


// export const saveFavoriteItem = async (userId, group, item) => {
//   let favorites = await getFavorites(userId, group)
//   if ( !favorites.includes(item)) {
//     favorites = [...favorites, item]
//     await saveUserPreferences(userId, getFavoritesFileName(group), { favorites })
//   }
//   return favorites
// }


// export const removeFavoriteItem = async (userId, group, item) => {
//   let favorites = await getFavorites(userId, group)
//   const index = favorites.indexOf(item)
//   if (index > -1) {
//     favorites.splice(index, 1);
//     await saveUserPreferences(userId, getFavoritesFileName(group), { favorites })
//   }
//   return favorites
// }


export const saveFavoriteItem = async (userId, group, item) => {
  let favorites = await getFavorites(userId, group)

  const index = favorites.findIndex(fav =>
    typeof fav === "string"
      ? fav === item.id
      : fav?.id === item.id
  )

  if (index >= 0) {
    // If current matching favorite was saved as a string, repair it
    if (typeof favorites[index] === "string") {
      favorites[index] = item
    } else {
      // Already exists as an object → nothing to do
      return favorites
    }
  } else {
    favorites = [...favorites, item]
  }

  await saveUserPreferences(
    userId,
    getFavoritesFileName(group),
    { favorites }
  )

  return favorites
}

export const removeFavoriteItem = async (userId, group, item) => {
  let favorites = await getFavorites(userId, group)

  const index = favorites.findIndex(fav =>
    typeof fav === "string"
      ? fav === item.id
      : fav?.id === item.id
  )

  if (index > -1) {
    favorites.splice(index, 1)

    await saveUserPreferences(
      userId,
      getFavoritesFileName(group),
      { favorites }
    )
  }

  return favorites
}
